/**
 * The record's ledger as its own section (SA-461, sweep spec §7.4, §9):
 * closed by default, because the process band's list mode now tells the
 * story. One React Query read (`useRecordEvents`, the band's key, so the
 * endpoint is called once per record view); the rows are `QueuePanel`'s
 * Timeline moved here — `describeProcessEvent` for the two events that
 * carry an AI's hand, the labels table for everything else.
 * A quiet block since SA-1059 (UX pass spec move 1.4): the ledger is read,
 * never acted on, so it has no box.
 */
import { recordErrorMessage, useRecordEvents } from '@/hooks/useRecordExtras'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { describeEvent } from './eventLabels'
import { readSummary, readUnavailable } from './readState'
import { RecordSection } from './RecordSection'
import { activitySummary } from './sectionSummaries'

export function ActivitySection({ orderId, focused = false }: { orderId: string; focused?: boolean }) {
  const events = useRecordEvents(orderId)
  const noun = useRecordNoun().singular
  const list = events.data
  return (
    <RecordSection id="activity" title="Activity" chrome="quiet" summary={readSummary(events, (d) => activitySummary(d, noun))} defaultOpen={false} focused={focused}>
      {events.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {readUnavailable(events) && <p className="text-sm text-destructive">{recordErrorMessage(events.error)}</p>}
      {list && list.length === 0 && <p className="text-sm text-muted-foreground">No events yet.</p>}
      {list && list.length > 0 && (
        <ul className="space-y-1">
          {list.map((e) => {
            const text = describeEvent(e.eventType, e.payload, noun)
            return (
              <li key={e.id} className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{text.label}</span>
                <span className="text-xs"> · {new Date(e.occurredAt).toLocaleString()}</span>
                {text.detail && <p className="text-xs">{text.detail}</p>}
              </li>
            )
          })}
        </ul>
      )}
    </RecordSection>
  )
}
